import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/shared/material.module';
import { ConfirmationService } from 'src/app/shared/services/utilities/confirmation.service';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { SnackBarService } from 'src/app/shared/services/snack-service.service';
import { mapTemplateError } from '../template-error';
import { TemplateFormDialogComponent, TemplateFormDialogData } from '../template-form-dialog/template-form-dialog.component';
import { TemplateNodeDialogComponent, TemplateNodeDialogData } from '../template-node-dialog/template-node-dialog.component';
import { PreviewNode, buildPreviewTree, descendantIds } from '../template-tree';
import {
  WorkClassificationTemplate,
  WorkClassificationTemplateDetail,
  WorkClassificationTemplateNode,
} from '../work-classification-template.models';
import { WorkClassificationTemplateService } from '../work-classification-template.service';

const T = 'WORKCLASSIFICATION.TEMPLATES.';

/** Edits one template's nodes. Edits never touch any project. */
@Component({
  selector: 'app-template-editor',
  templateUrl: './template-editor.component.html',
  styleUrls: ['./template-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MaterialModule, TranslatePipe, RouterLink, NgTemplateOutlet],
})
export class TemplateEditorComponent implements OnInit {
  private service = inject(WorkClassificationTemplateService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private confirm = inject(ConfirmationService);
  private snackBar = inject(SnackBarService);
  private translate = inject(TranslateService);
  private logger = inject(LoggerService);
  private destroyRef = inject(DestroyRef);

  templateId = 0;
  template = signal<WorkClassificationTemplate | null>(null);
  nodes = signal<WorkClassificationTemplateNode[]>([]);
  loading = signal(true);
  expanded = signal<ReadonlySet<number>>(new Set());
  tree = computed<PreviewNode[]>(() => buildPreviewTree(this.nodes()));

  ngOnInit(): void {
    this.templateId = Number(this.route.snapshot.paramMap.get('id'));
    this.load();
  }

  load(): void {
    this.service
      .get(this.templateId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: detail => this.applyDetail(detail),
        error: err => {
          this.loading.set(false);
          const m = mapTemplateError(err);
          this.snackBar.error(this.translate.instant(m.key, m.params));
          if (m.code === 'NOT_FOUND') {
            this.router.navigate(['/worktime', 'classification', 'templates']);
          } else {
            this.logger.errorWithContext('TemplateEditorComponent', 'Failed to load template', err);
          }
        },
      });
  }

  private applyDetail(detail: WorkClassificationTemplateDetail): void {
    this.template.set(detail.template);
    this.nodes.set(detail.nodes);
    this.loading.set(false);
  }

  isExpanded(id: number): boolean {
    return this.expanded().has(id);
  }

  toggle(id: number): void {
    const next = new Set(this.expanded());
    if (!next.delete(id)) {
      next.add(id);
    }
    this.expanded.set(next);
  }

  private expand(id: number | null): void {
    if (id != null && !this.expanded().has(id)) {
      this.expanded.set(new Set(this.expanded()).add(id));
    }
  }

  editInfo(): void {
    const data: TemplateFormDialogData = { mode: 'edit', template: this.template()! };
    this.dialog
      .open<TemplateFormDialogComponent, TemplateFormDialogData, WorkClassificationTemplateDetail>(TemplateFormDialogComponent, {
        width: '36rem',
        maxWidth: '95vw',
        panelClass: ['mobile-full-dialog'],
        data,
      })
      .afterClosed()
      .subscribe(detail => {
        if (detail) {
          this.template.set(detail.template);
          this.snackBar.success(this.translate.instant(T + 'MSG.UPDATED'));
        }
      });
  }

  addNode(parentId: number | null): void {
    this.openNodeDialog({ templateId: this.templateId, nodes: this.nodes(), parentId }, parentId);
  }

  editNode(id: number): void {
    const node = this.nodes().find(n => n.id === id);
    if (node) {
      this.openNodeDialog({ templateId: this.templateId, nodes: this.nodes(), node }, node.parentId);
    }
  }

  private openNodeDialog(data: TemplateNodeDialogData, expandAfter: number | null): void {
    this.dialog
      .open<TemplateNodeDialogComponent, TemplateNodeDialogData, WorkClassificationTemplateNode>(TemplateNodeDialogComponent, {
        width: '36rem',
        maxWidth: '95vw',
        panelClass: ['mobile-full-dialog'],
        data,
      })
      .afterClosed()
      .subscribe(saved => {
        if (saved) {
          this.expand(saved.parentId ?? expandAfter);
          this.load();
        }
      });
  }

  deleteNode(id: number): void {
    const node = this.nodes().find(n => n.id === id);
    if (!node) {
      return;
    }
    // Only leaves can be deleted (the UI also hides the button for nodes with children).
    if (descendantIds(this.nodes(), id).size > 0) {
      return;
    }
    this.confirm.confirm(
      this.translate.instant(T + 'EDITOR.CONFIRM_DELETE_NODE', { name: node.name }),
      this.translate.instant(T + 'EDITOR.DELETE_NODE'),
      () => {
        this.service.deleteNode(this.templateId, id).subscribe({
          next: () => this.load(),
          error: err => {
            const m = mapTemplateError(err);
            this.snackBar.error(this.translate.instant(m.key, m.params));
          },
        });
      },
      () => {},
      this.translate.instant('COMMON.DELETE'),
      'warn',
      'delete',
    );
  }
}
